import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PenLine, Trash2, CheckCircle2, Save } from "lucide-react";

// =============================================================================
// EmployeeSignaturePad — captures the driver's signature once.
// Auto-Generator copies this signature onto every generated time_entry +
// driving_log for the whole month.
// =============================================================================
const CANVAS_W = 600;
const CANVAS_H = 200;

export default function EmployeeSignaturePad({ employeeId, employeeName, initialSignature, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!initialSignature);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setHasSignature(!!initialSignature);
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    if (initialSignature) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = initialSignature;
    }
  }, [initialSignature]);

  function pos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * (c.width / rect.width),
      y: (t.clientY - rect.top) * (c.height / rect.height),
    };
  }

  function start(e) {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#f4f1ea";
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }
  function move(e) {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }
  function end() {
    if (drawing) setHasSignature(true);
    setDrawing(false);
  }
  function clear() {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
    setSavedAt(null);
  }

  async function save() {
    const c = canvasRef.current;
    if (!hasSignature) {
      toast.error("Bitte zuerst unterschreiben");
      return;
    }
    const dataUrl = c.toDataURL("image/png");
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${employeeId}/signature`, {
        signature_data_url: dataUrl,
      });
      setSavedAt(data.captured_at);
      toast.success("Signatur gespeichert — wird ab jetzt automatisch auf alle generierten Tage angewendet");
      onChange?.(dataUrl);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm("Signatur wirklich löschen? Zukünftige Auto-Generierungen werden ohne Signatur erstellt.")) return;
    setBusy(true);
    try {
      await api.delete(`/employees/${employeeId}/signature`);
      clear();
      onChange?.(null);
      toast.success("Signatur gelöscht");
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    } finally { setBusy(false); }
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5" data-testid="employee-signature-pad">
      <div className="flex items-start gap-2.5 mb-4">
        <PenLine className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-display font-semibold text-zinc-50">Fahrer-Signatur</h3>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-xl">
            Einmaliges Unterschreiben — diese Signatur wird auf jeden Zeiterfassungs- und
            Lenkprotokoll-Eintrag angewendet, der durch den Auto-Generator für {employeeName} erzeugt wird.
          </p>
        </div>
      </div>

      <div className="border border-zinc-700 rounded bg-zinc-950 inline-block">
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          data-testid="employee-signature-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ width: "100%", maxWidth: CANVAS_W, height: "auto", touchAction: "none", cursor: "crosshair" }}
        />
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button onClick={clear} data-testid="signature-clear"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">
          <Trash2 className="w-3.5 h-3.5" /> Zurücksetzen
        </button>
        <button onClick={save} disabled={busy || !hasSignature}
          data-testid="signature-save"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded font-medium disabled:opacity-40">
          <Save className="w-3.5 h-3.5" /> {busy ? "Speichere …" : "Signatur speichern"}
        </button>
        {!!initialSignature && (
          <button onClick={remove} disabled={busy}
            data-testid="signature-delete"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-300 rounded">
            <Trash2 className="w-3.5 h-3.5" /> Vorhandene Signatur löschen
          </button>
        )}
        {(savedAt || initialSignature) && (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
            <CheckCircle2 className="w-3.5 h-3.5" /> Signatur hinterlegt
          </span>
        )}
      </div>
    </div>
  );
}
